const DAY = 1440;
const formatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Amsterdam',
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
});

export interface CityTimeSnapshot {
  readonly minutes: number;
  readonly dayOfYear: number;
  readonly utcOffset: number;
  readonly live: boolean;
  readonly revision: number;
}

export const wrapMinutes = (minutes: number): number => ((minutes % DAY) + DAY) % DAY;

export function amsterdamTime(epoch: number): Omit<CityTimeSnapshot, 'live' | 'revision'> {
  const parts = Object.fromEntries(
    formatter.formatToParts(epoch).map((p) => [p.type, Number(p.value)])
  );
  const year = parts.year ?? 1970,
    month = parts.month ?? 1,
    day = parts.day ?? 1;
  const hour = parts.hour ?? 0,
    minute = parts.minute ?? 0,
    second = parts.second ?? 0;
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  return {
    minutes: hour * 60 + minute + second / 60,
    dayOfYear: Math.floor((Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 0)) / 86400000),
    utcOffset: Math.round((wall - epoch) / 60000),
  };
}

/** Live Amsterdam civil time, or a selected time that continues at normal speed. */
export class CityTime {
  private selectionEnabled = true;
  private sharedNow: (() => number) | null = null;
  private selected: { minutes: number; epoch: number } | null = null;
  private revision = 0;
  private second = -Infinity;
  private current: ReturnType<typeof amsterdamTime> | null = null;

  constructor(
    private readonly now = Date.now,
    initial?: number
  ) {
    if (initial !== undefined) this.set(initial);
  }

  snapshot(): CityTimeSnapshot {
    const epoch = this.sharedNow?.() ?? this.now();
    if (Math.floor(epoch / 1000) !== this.second || !this.current) {
      this.current = amsterdamTime(epoch);
      this.second = Math.floor(epoch / 1000);
    }
    const current = this.current;
    return {
      ...current,
      live: this.selected === null,
      revision: this.revision,
      minutes: this.selected
        ? wrapMinutes(this.selected.minutes + (epoch - this.selected.epoch) / 60000)
        : current.minutes,
    };
  }

  set(minutes: number): void {
    if (!this.editable) return;
    if (!Number.isFinite(minutes)) return;
    this.selected = { minutes: wrapMinutes(minutes), epoch: this.sharedNow?.() ?? this.now() };
    this.revision++;
  }

  reset(): void {
    if (!this.editable) return;
    this.selected = null;
    this.revision++;
  }

  get shared(): boolean {
    return this.sharedNow !== null;
  }

  get editable(): boolean {
    return this.selectionEnabled && !this.shared;
  }

  lock(): void {
    this.selectionEnabled = false;
    this.selected = null;
    this.revision++;
  }

  unlock(): void {
    this.selectionEnabled = true;
    this.follow(null);
  }

  follow(now: (() => number) | null): void {
    this.sharedNow = now;
    this.selected = null;
    this.second = -Infinity;
    this.revision++;
  }
}

export function formatCityTime(minutes: number): string {
  const value = Math.floor(wrapMinutes(minutes));
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
}

/** Degrees clockwise from XII of the dial's two hands, at the whole minute the dial shows. */
export function handAngles(minutes: number): { readonly hour: number; readonly minute: number } {
  const value = Math.floor(wrapMinutes(minutes));
  return { hour: (value / 2) % 360, minute: (value % 60) * 6 };
}

/** Unwrap a twelve-hour dial so dragging across XII preserves the chosen half-day. */
export function dialMinutes(x: number, y: number, current: number): number {
  const turn = (Math.atan2(x, -y) / (2 * Math.PI) + 1) % 1;
  const target = turn * 720;
  const delta = ((target - (current % 720) + 1080) % 720) - 360;
  return wrapMinutes(Math.round(current + delta));
}
